<template>
  <!--
    BaseTable flexível
    Props:
      columns: [
        {
          text: 'Título',
          value: 'campo',
          align: 'start' | 'end',
          width: '320px' | '20%' | 120, // px ou % (string ou number)
          tooltip: 'Texto do tooltip',
          headerSlot: 'nomeDoSlot',
        },
        ...
      ]
    Slots:
      - Para célula: <template #campoCell="{ row }">...</template>
      - Para header customizado: <template #nomeDoSlot>...</template>
    Observações:
      - Se width não for informado, a primeira coluna será 320px, as demais 120px.
      - O valor pode ser string (ex: '200px', '20%') ou number (em px).
  -->
  <div class="table-wrapper">
    <v-container
      class="empty-state-container"
      v-if="!loading && !isSearching && (!items.length || items.length === 0)"
    >
      <v-row class="empty-state-content" justify="center" align="center">
        <span class="titleEmptyState">
          {{ $t('message.pages.analyticsV3.noResultsFound') }}
        </span>
        <span class="subtitleEmptyState">
          {{ $t('message.pages.analyticsV3.adjustingFilter') }}
        </span>
      </v-row>
    </v-container>

    <v-container
      class="empty-state-container"
      v-if="!loading && isSearching && (!items.length || items.length === 0)"
    >
      <v-row class="empty-state-content" justify="center" align="center">
        <span class="titleEmptyState">
          {{ $t('message.pages.analyticsV3.noResultsFound') }}
        </span>
        <span class="subtitleEmptyState">
          {{ $t('message.pages.analyticsV3.adjustingSearch') }}
        </span>
        <base-btn type="tertiary" @click="emitsClearSearch" class="mt-2">
          {{ $t('message.pages.analyticsV3.clearSearch') }}
        </base-btn>
      </v-row>
    </v-container>

    <template v-if="loading || items.length > 0">
      <v-simple-table class="base-table">
        <thead>
          <tr>
            <th
              v-for="(column, index) in visibleColumns"
              :key="index"
              :style="
                columnStyle(index, column) +
                'text-align: ' +
                (resolveAlign(index, column) === 'end'
                  ? 'right;'
                  : 'left; margin-left: 1rem;')
              "
              :class="{
                'header-cell': true,
              }"
              @click="toggleSort(column.value)"
              @mouseenter="hoveredHeader = column.value"
              @mouseleave="hoveredHeader = null"
            >
              <span
                class="body-label-xs d-flex ga-2 align-center"
                :style="`
                      line-height: 110%;
                      justify-content: ${
                        resolveAlign(index, column) === 'end'
                          ? 'flex-end'
                          : 'flex-start'
                      };
                      font-size: ${column.fontSize || '13px'};
                    `"
                :class="column.color || 'blueMarine-base--text'"
              >
                <template v-if="column.headerSlot">
                  <div class="ml-2">
                    <slot :name="column.headerSlot" />
                  </div>
                </template>
                <template v-else>
                  <span v-if="index === 0 || column.text === 'Play rate'">{{
                    column.text
                  }}</span>
                  <span
                    v-else
                    v-html="insertBreakAfterFirstWord(column.text)"
                  />
                </template>

                <v-tooltip max-width="200px" top :disabled="!column.tooltip">
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      v-if="column.tooltip"
                      size="12"
                      color="neutral-70"
                      v-on="on"
                      v-bind="attrs"
                      class="mt-0 ml-2"
                    >
                      mdi-information
                    </v-icon>
                  </template>
                  <span>{{ column.tooltip }}</span>
                </v-tooltip>

                <v-icon
                  size="14"
                  :color="
                    hoveredHeader === column.value
                      ? 'neutral-40-light'
                      : 'transparent'
                  "
                  class="mt-0 ml-1 sort-icon"
                  :style="`min-width: 14px; transition: color 0.2s ease;`"
                >
                  <template v-if="sortColumn === column.value">
                    {{
                      sortOrder === 'asc'
                        ? 'mdi-chevron-up'
                        : 'mdi-chevron-down'
                    }}
                  </template>
                  <template v-else> mdi-chevron-down </template>
                </v-icon>
              </span>
            </th>
            <th
              v-if="showActionColumn"
              class="header-cell action-header-cell"
              style="text-align: right !important"
            >
              <slot name="actionHeader">
                <v-menu
                  v-model="columnsMenu"
                  :position-x="columnsMenuX"
                  :position-y="columnsMenuY"
                  absolute
                  offset-y
                  :close-on-content-click="false"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-icon
                      class="columnIcon neutral-40-light--text"
                      size="16"
                      v-bind="attrs"
                      v-on="{
                        ...on,
                        click: (e) => {
                          e.stopPropagation()
                          openColumnsMenu(e)
                        },
                      }"
                      style="cursor: pointer"
                    >
                      panda-columns
                    </v-icon>
                  </template>
                  <v-list class="columnDisplay">
                    <v-list-item
                      v-for="col in columns.slice(1)"
                      :key="col.value"
                      @click="toggleColumn(col.value)"
                    >
                      <v-list-item-action style="margin-right: 16px">
                        <v-icon size="20" v-if="isColumnVisible(col.value)">
                          mdi-check
                        </v-icon>
                      </v-list-item-action>
                      <v-list-item-title
                        class="body-regular-small neutral-20-dark--text"
                      >
                        {{ col.text }}
                      </v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </slot>
            </th>
          </tr>
        </thead>
        <tbody>
          <template v-if="loading">
            <tr v-for="i in itemsPerPage" :key="'loading-' + i">
              <td
                v-for="(column, colIndex) in visibleColumns"
                :key="colIndex"
                :style="columnStyle(colIndex, column)"
                :class="{
                  'text-left': resolveAlign(colIndex, column) === 'start',
                  'text-right': resolveAlign(colIndex, column) === 'end',
                }"
              >
                <v-skeleton-loader
                  type="text"
                  class="my-2"
                  height="20px"
                  :width="colIndex === 0 ? '50%' : '20%'"
                  :style="
                    resolveAlign(colIndex, column) === 'end'
                      ? 'margin-left:auto; margin-right:0;'
                      : ''
                  "
                />
              </td>
              <td
                v-if="showActionColumn"
                style="
                  width: 30px;
                  min-width: 30px;
                  max-width: 30px;
                  text-align: right !important;
                "
              >
                <v-skeleton-loader
                  type="text"
                  class="my-2"
                  height="20px"
                  width="20px"
                />
              </td>
            </tr>
          </template>

          <template v-else>
            <template v-for="(item, rowIndex) in paginatedItems">
              <tr
                :key="rowIndex"
                @mouseenter="hoveredRow = rowIndex"
                @mouseleave="hoveredRow = null"
                @click="$emit('row-click', item, rowIndex)"
                :class="{
                  'row-container--expanded': item.expanded,
                }"
                :style="noPointer ? 'cursor: default' : ''"
              >
                <td
                  v-for="(column, colIndex) in visibleColumns"
                  :key="colIndex"
                  :style="columnStyle(colIndex, column)"
                  :class="{
                    'text-left': resolveAlign(colIndex, column) === 'start',
                    'text-right': resolveAlign(colIndex, column) === 'end',
                  }"
                >
                  <slot :name="`${column.value}Cell`" :row="item">
                    <span class="text-truncate" :title="item[column.value]">
                      {{ item[column.value] }}
                    </span>
                  </slot>
                </td>
                <td
                  v-if="showActionColumn"
                  style="
                    width: 30px;
                    min-width: 30px;
                    max-width: 30px;
                    text-align: right !important;
                  "
                >
                  <slot
                    name="actionCell"
                    :row="item"
                    :index="rowIndex"
                    :hovered="hoveredRow === rowIndex"
                  ></slot>
                </td>
              </tr>
              <tr
                v-if="item.expanded"
                :key="`expanded-${rowIndex}`"
                class="expanded-row"
              >
                <td
                  :colspan="visibleColumns.length + (showActionColumn ? 1 : 0)"
                >
                  <slot name="expandedRow" :row="item" :rowIndex="rowIndex">
                    {{ item.expanded }}
                  </slot>
                </td>
              </tr>
            </template>
            <span v-if="showPagination">
              <tr
                v-for="i in itemsPerPage - paginatedItems.length"
                :key="'empty-' + i"
                class="empty-row"
              >
                <td
                  v-for="(column, colIndex) in visibleColumns"
                  :key="colIndex"
                  style="border-bottom: 0px !important"
                >
                  &nbsp;
                </td>
                <td
                  v-if="showActionColumn"
                  style="border-bottom: 0px !important"
                >
                  &nbsp;
                </td>
              </tr>
            </span>
          </template>
        </tbody>
      </v-simple-table>

      <div v-if="!loading && showPagination" class="table-summary my-2">
        <div class="body-regular-small recordsNumber">
          {{ sortedItems.length }}
          {{
            $t('message.pages.analytics.bandwidthDetailsModal.recordsOfConsume')
          }}
          ({{ page }}
          {{ $t('message.introJS.options.stepNumbersOfLabel') }}
          {{ pageCount }})
        </div>

        <v-pagination
          v-model="page"
          :length="pageCount"
          :total-visible="5"
          elevation="0"
          class="paginationTable"
          prev-icon="mdi-arrow-left"
          next-icon="mdi-arrow-right"
        />

        <span />
      </div>
    </template>
  </div>
</template>

<script>
export default {
  name: 'BaseTable',
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    columns: {
      type: Array,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    isSearching: {
      type: Boolean,
      default: false,
    },
    headerHeight: {
      type: [Number, String],
      default: 45,
    },
    itemsPerPage: {
      type: Number,
      default: 10,
    },
    showPagination: {
      type: Boolean,
      default: true,
    },
    noPointer: {
      type: Boolean,
      default: false,
    },
    sumField: {
      type: String,
      default() {
        return this.columns && this.columns[1] ? this.columns[1].value : null
      },
    },

    showTotalLabel: {
      type: Boolean,
      default: true,
    },
    rowHover: {
      type: Boolean,
      default: false,
    },
    showActionColumn: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sortColumn:
        this.columns && this.columns.length > 1 ? this.columns[1].value : null,
      sortOrder: 'desc',
      page: 1,
      hoveredRow: null,
      hoveredHeader: null,
      columnsVisibility: (this.columns || []).map((col) => ({
        key: col.value,
        visible: true,
      })),
      columnsMenu: false,
      columnsMenuX: 0,
      columnsMenuY: 0,
    }
  },
  watch: {
    columns: {
      handler(newCols) {
        if (!Array.isArray(newCols)) newCols = []
        // Atualiza columnsVisibility se as colunas mudarem
        this.columnsVisibility = newCols.map((col) => {
          const found = (this.columnsVisibility || []).find(
            (c) => c.key === col.value
          )
          return found ? found : { key: col.value, visible: true }
        })

        if (!this.sortColumn && newCols.length > 1) {
          this.sortColumn = newCols[1].value
          this.sortOrder = 'desc'
        }
      },
      deep: true,
    },
    visibleColumns: {
      handler(newVisibleColumns) {
        this.$emit('update-visible-columns', newVisibleColumns)
      },
      immediate: true,
      deep: true,
    },
  },
  computed: {
    visibleColumns() {
      const cols = Array.isArray(this.columns) ? this.columns : []
      return cols.filter((col) => {
        const found = (this.columnsVisibility || []).find(
          (c) => c.key === col.value
        )
        return found ? found.visible : true
      })
    },
    sortedItems() {
      const { items, sortColumn, sortOrder } = this
      if (!sortColumn) {
        return this.filterEmptyRows(items)
      }

      const asc = sortOrder === 'asc'
      const sorted = items.slice().sort((a, b) => {
        const valA = a[sortColumn]
        const valB = b[sortColumn]

        const aNull = valA == null
        const bNull = valB == null
        if (aNull || bNull) {
          if (aNull && bNull) return 0
          return aNull === asc ? -1 : 1
        }

        if (typeof valA === 'number' && typeof valB === 'number') {
          return asc ? valA - valB : valB - valA
        }

        const numA = parseFloat(valA)
        const numB = parseFloat(valB)
        if (!Number.isNaN(numA) && !Number.isNaN(numB)) {
          return asc ? numA - numB : numB - numA
        }

        const strA = String(valA)
        const strB = String(valB)
        return asc ? strA.localeCompare(strB) : strB.localeCompare(strA)
      })

      return this.filterEmptyRows(sorted)
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.sortedItems.length / this.itemsPerPage))
    },
    paginatedItems() {
      const start = (this.page - 1) * this.itemsPerPage
      return this.sortedItems.slice(start, start + this.itemsPerPage)
    },
    totalSum() {
      if (!this.sumField) return 0
      return this.sortedItems.reduce((acc, item) => {
        const val = Number(item[this.sumField])
        return acc + (isNaN(val) ? 0 : val)
      }, 0)
    },
  },
  filters: {
    numberFormat(value) {
      if (typeof value !== 'number') return value
      return value.toLocaleString()
    },
  },
  methods: {
    emitsClearSearch() {
      this.$emit('clear-search')
    },
    toggleSort(prop) {
      // DEBUG: log para depuração
      console.log('Ordenando por:', prop, 'Exemplo de dados:', this.items[0])
      if (this.sortColumn === prop) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortColumn = prop
        this.sortOrder = 'desc'
      }
      this.page = 1
    },
    columnStyle(index, column) {
      let width = column.width
      if (!width) {
        if (index === 0) width = '320px'
        else width = '120px'
      } else if (typeof width === 'number') {
        width = width + 'px'
      }

      // Para a primeira coluna, garantir largura fixa
      if (index === 0) {
        return 'width: 320px !important; min-width: 320px !important; max-width: 320px !important; flex: 0 0 320px !important;'
      }

      return `width: ${width}; min-width: ${width}; max-width: ${width};`
    },
    resolveAlign(index, column) {
      if (column.align) return column.align
      return index === 0 ? 'start' : 'end'
    },
    insertBreakAfterFirstWord(text) {
      if (!text) return ''
      const [first, ...rest] = text.split(' ')
      return `${first}<br />${rest.join(' ')}`
    },
    toggleColumn(key) {
      const idx = this.columns.findIndex((c) => c.value === key)
      if (idx === 0) return // Nunca permite esconder a primeira coluna
      const col = this.columnsVisibility.find((c) => c.key === key)
      if (col) col.visible = !col.visible
    },
    isColumnVisible(key) {
      const col = this.columnsVisibility.find((c) => c.key === key)
      return col ? col.visible : false
    },
    openColumnsMenu(e) {
      this.columnsMenuX = e.clientX
      this.columnsMenuY = e.clientY
      this.columnsMenu = true
    },
    filterEmptyRows(items) {
      return items.filter((item) => {
        return this.visibleColumns.some((column) => {
          const value = item[column.value]

          if (typeof value === 'number') {
            return value > 0
          }

          if (typeof value === 'string') {
            const numValue = parseFloat(value)
            if (!Number.isNaN(numValue)) {
              return numValue > 0
            }
          }
          return true
        })
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.table-wrapper {
  width: 100%;
  max-width: 100%;
  overflow-x: auto;
}
.base-table {
  width: 100%;
  table-layout: fixed;
  max-width: 100%;
}
.base-table > .v-data-table__wrapper > table > thead > tr > th {
  padding: 0 8px !important;
}
.base-table > .v-data-table__wrapper > table > tbody > tr.empty-row > td {
  pointer-events: none !important;
  border-bottom: 0px !important;
}

.text-truncate {
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.header-cell {
  border-bottom: 1px solid var(--v-neutral-lighten1, #e0e0e0);
  background: varFigma(neutral-100-white);
  font-size: 13px;
  font-weight: 500;
  height: 45px;
  vertical-align: middle;
  margin-left: 1rem;
  padding: 0 8px;
  cursor: pointer;
}

.body-label-xs {
  font-weight: 500;
  color: var(--v-blueMarine-base, #1a237e);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
  position: relative;
}
.table-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
  color: var(--v-neutral-lighten1, #444);
  padding: 0 8px;
  min-height: 40px;
}
.recordsNumber {
  color: var(--v-neutral-lighten1, #444);
}

.base-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:not(.expanded-row)
  > td:not(.text-right) {
  padding: 0 8px !important;
  z-index: 2;
}

.base-table
  > .v-data-table__wrapper
  > table
  > tbody
  > tr:not(.expanded-row)
  > td.text-right {
  padding: 0 28px 0 8px !important;
}

.base-table > .v-data-table__wrapper > table > tbody > tr.expanded-row > td {
  padding: 0 !important;
}

.v-data-table
  .v-data-table__wrapper
  > table
  > tbody
  > tr:not(:last-child).row-container--expanded
  > td:not(.v-data-table__mobile-row) {
  border-bottom: none !important;
}

@media (min-width: 1200px) {
  .base-table >>> .v-data-table__wrapper {
    overflow-x: hidden !important;
  }
}
.row-hover:hover {
  background-color: varFigma(neutral-99) !important;
  cursor: pointer;
  transition: background 0.15s;
}
.v-data-table
  .v-data-table__wrapper
  > table
  > tbody
  > tr:hover:not(.v-data-table__expanded__content):not(.v-data-table__empty-wrapper):not(.expanded-row):hover {
  background-color: varFigma(neutral-99) !important;
  cursor: pointer;
  transition: background 0.15s;
}
.empty-state-container {
  height: 376px;
  min-height: 376px;
  max-height: 376px;
  padding: 0;
  margin: 0 auto;
  overflow: hidden;
}

.empty-state-content {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.titleEmptyState {
  font-size: 16px;
  line-height: 150%;
  font-weight: 400;
  color: varFigma(neutral-20-dark);
  margin-bottom: 4px;
  margin-top: 0 !important;
}

.subtitleEmptyState {
  font-size: 14px;
  line-height: 150%;
  font-weight: 400;
  color: varFigma(neutral-30-base);
}

.action-header-cell {
  background: varFigma(neutral-100-white);
  border-bottom: thin solid rgba(255, 255, 255, 0.12);
  padding: 0;
  text-align: center;
  min-width: 50px !important;
  width: 50px !important;
  max-width: 50px !important;
}

.sort-icon {
  transition: color 0.2s ease;
  min-width: 14px;
  width: 14px;
  flex-shrink: 0;
}

.sort-icon:hover {
  transform: scale(1.1);
}

.header-cell:hover .sort-icon {
  opacity: 1;
}

/* Garantir que a primeira coluna seja estável */
.base-table > .v-data-table__wrapper > table > thead > tr > th:first-child,
.base-table > .v-data-table__wrapper > table > tbody > tr > td:first-child {
  width: 320px !important;
  min-width: 320px !important;
  max-width: 320px !important;
  flex: 0 0 320px !important;
  box-sizing: border-box !important;
}
.row-container--expanded {
  border: 1px solid varFigma(neutral-95-lightest);
  border-bottom: none;
  border-radius: 8px 8px 0 0;
}
</style>
